import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  doc,
  setDoc,
  updateDoc,
  deleteField,
  serverTimestamp,
  Timestamp,
  getDoc,
  increment,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { createConversation, getOrCreateSupportConversation } from './chat';
import { logAdminAction } from './adminActions';
import type { UserProfile, CoachVerificationStatus, UserRole } from '../types/user';
import type { LessonHistoryEvent, OrderHistoryEvent } from '../types/lesson';

// Type alias for admin user data (same as UserProfile for now)
export type AdminUserData = UserProfile;

export interface Actor {
  id: string;
  name: string;
  role: UserRole;
}

export interface LessonData {
  id: string;
  coachId: string;
  coachName: string;
  playerId: string;
  playerName: string;
  status: 'reserved' | 'in_progress' | 'delivered';
  offeringId?: string;
  offeringTitle?: string;
  createdAt: string;
  reservedAt?: string;
  deliveredAt?: string;
  conversationId: string;
  // Order context
  orderId?: string;
  orderType?: string;       // 'single_lesson' | 'shot_consult' | 'training_program'
  lessonNumber?: number;
  totalLessons?: number;
  /** Current coach on the parent order — may differ from lesson.coachId for delivered lessons after a transfer. */
  orderCoachId?: string;
  orderCoachName?: string;
  // Transfer state
  isTransferred?: boolean;
  transferredFromCoachName?: string;
  transferNote?: string;
  /** True when the parent order has been refunded — Transfer action should be suppressed. */
  orderRefunded?: boolean;
  // Video content
  playerVideoUrl?: string;
  playerVideoStoragePath?: string;
  publishedOriginalVideoId?: string;
  publishedDraftId?: string;
  coachVideoUrl?: string;
  coachVideoStoragePath?: string;
  coachNotes?: string;
  // Async video composition tracking
  videoProcessingStatus?: 'pending' | 'processing' | 'complete' | 'failed';
  videoProcessingError?: string;
  /* Submission intent. Distinct from videoProcessingStatus, which is only ever
     written once a submission has already got past the point that loses them. */
  submissionStatus?: 'submitting' | 'delivered' | 'failed';
  submissionStartedAt?: string;
  submissionError?: string;
}

export interface ChatData {
  id: string;
  participants: string[];
  participantDetails: Record<string, { name: string; avatarUrl?: string }>;
  lastMessage?: string;
  lastMessageAt?: Timestamp;
}

export interface MetricsData {
  totalLessons: number;
  lessonsPerCoach: Record<string, number>;
  lessonsPerPlayer: Record<string, number>;
  totalUsers: number;
  totalCoaches: number;
  totalPlayers: number;
}

const STAFF_ROLES = new Set(['admin', 'support', 'sales', 'accounts', 'root']);
const DOUBLE_BAGEL_UID = 'double-bagel-support';

/**
 * Get all users with optional filtering — excludes staff accounts (admin/support/root)
 */
export async function getAllUsers(
  role?: 'coach' | 'player',
  activeOnly: boolean = false
): Promise<AdminUserData[]> {
  // When filtering by role we omit orderBy — combining where() on one field with
  // orderBy() on another requires a composite index that doesn't exist for every
  // role/createdAt combination. Sorting is done client-side instead (same pattern
  // as the activeOnly filter below).
  const baseQuery = role
    ? query(collection(db, 'users'), where('role', '==', role))
    : query(collection(db, 'users'), orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(baseQuery);
  let users = snapshot.docs
    .filter(doc => doc.id !== DOUBLE_BAGEL_UID && !STAFF_ROLES.has(doc.data().role))
    .map(doc => {
      const data = doc.data();
      return {
        ...data,
        uid: doc.id,
        createdAt: data.createdAt?.toDate?.().toISOString() ?? data.createdAt,
      };
    }) as AdminUserData[];

  if (role) {
    users.sort((a, b) => {
      const aTs = typeof a.createdAt === 'string' ? a.createdAt : '';
      const bTs = typeof b.createdAt === 'string' ? b.createdAt : '';
      return bTs.localeCompare(aTs);
    });
  }

  if (activeOnly) {
    users = users.filter(user => user.isActive !== false);
  }

  return users;
}

/**
 * Toggle user active status — root users can never be deactivated
 */
export async function toggleUserActive(userId: string, isActive: boolean, actor?: Actor): Promise<void> {
  const userRef = doc(db, 'users', userId);
  const snap = await getDoc(userRef);
  const data = snap.data();
  if (data?.role === 'root') throw new Error('The root user cannot be deactivated');
  const previousIsActive = data?.isActive !== false;
  await updateDoc(userRef, { isActive, updatedAt: serverTimestamp() });
  if (actor) {
    await logAdminAction({
      actorId: actor.id, actorName: actor.name, actorRole: actor.role,
      action: isActive ? 'activate_user' : 'deactivate_user',
      actionLabel: isActive ? 'Activate User' : 'Deactivate User',
      targetType: 'user', targetCollection: 'users', targetId: userId,
      targetLabel: data?.displayName || data?.email || userId,
      changes: { isActive: { from: previousIsActive, to: isActive } },
    });
  }
}

/**
 * Get all staff accounts (admin/support/root roles)
 */
export async function getAllStaffUsers(): Promise<AdminUserData[]> {
  const snapshot = await getDocs(
    query(collection(db, 'users'), orderBy('createdAt', 'desc'))
  );
  return snapshot.docs
    .filter(doc => STAFF_ROLES.has(doc.data().role) && doc.id !== DOUBLE_BAGEL_UID)
    .map(doc => {
      const data = doc.data();
      return {
        ...data,
        uid: doc.id,
        createdAt: data.createdAt?.toDate?.().toISOString() ?? data.createdAt,
      };
    }) as AdminUserData[];
}

/**
 * Toggle coachProfile.foundingCoach on a single coach.
 */
export async function toggleFoundingCoach(coachId: string, foundingCoach: boolean, actor?: Actor): Promise<void> {
  const userRef = doc(db, 'users', coachId);
  const snap = await getDoc(userRef);
  const data = snap.data();
  const prev = data?.coachProfile?.foundingCoach ?? false;
  await updateDoc(userRef, { 'coachProfile.foundingCoach': foundingCoach, updatedAt: serverTimestamp() });
  if (actor) {
    await logAdminAction({
      actorId: actor.id, actorName: actor.name, actorRole: actor.role,
      action: foundingCoach ? 'mark_founding_coach' : 'unmark_founding_coach',
      actionLabel: foundingCoach ? 'Mark Founding Coach' : 'Remove Founding Coach',
      targetType: 'user', targetCollection: 'users', targetId: coachId,
      targetLabel: data?.coachProfile?.displayName || data?.displayName || data?.email || coachId,
      changes: { 'coachProfile.foundingCoach': { from: prev, to: foundingCoach } },
    });
  }
}

/**
 * Backfill coachProfile.foundingCoach = true for every verified coach that doesn't already have it.
 * Idempotent — coaches already flagged are skipped (no write).
 */
export async function backfillFoundingCoaches(
  actor?: Actor,
): Promise<{ updated: string[]; skipped: number; errors: number }> {
  const snap = await getDocs(
    query(collection(db, 'users'), where('coachProfile.verificationStatus', '==', 'verified')),
  );

  const toUpdate = snap.docs.filter((d) => d.data()?.coachProfile?.foundingCoach !== true);
  const skipped = snap.docs.length - toUpdate.length;

  const updated: string[] = [];
  let errors = 0;

  for (const docSnap of toUpdate) {
    try {
      await updateDoc(docSnap.ref, { 'coachProfile.foundingCoach': true, updatedAt: serverTimestamp() });
      const d = docSnap.data();
      updated.push(d?.coachProfile?.displayName || d?.displayName || docSnap.id);
    } catch {
      errors++;
    }
  }

  if (actor && updated.length > 0) {
    await logAdminAction({
      actorId: actor.id, actorName: actor.name, actorRole: actor.role,
      action: 'backfill_founding_coaches',
      actionLabel: 'Backfill Founding Coaches',
      targetType: 'user', targetCollection: 'users', targetId: 'bulk',
      targetLabel: `${updated.length} coaches`,
      changes: { 'coachProfile.foundingCoach': { from: false, to: true } },
    });
  }

  return { updated, skipped, errors };
}

/**
 * Verify a coach - sets verificationStatus to 'verified'
 * This grants the coach full access to coach features and allows profile switching.
 */
export async function verifyCoach(coachId: string, actor?: Actor): Promise<void> {
  const userRef = doc(db, 'users', coachId);
  const snap = await getDoc(userRef);
  const data = snap.data();
  const prevStatus = data?.coachProfile?.verificationStatus ?? null;
  const prevVerifiedAt = data?.coachProfile?.verifiedAt ?? null;
  const prevIsVerified = data?.isVerified ?? false;
  const verifiedAt = new Date().toISOString();
  await updateDoc(userRef, {
    'coachProfile.verificationStatus': 'verified' as CoachVerificationStatus,
    'coachProfile.verifiedAt': verifiedAt,
    isVerified: true,
    updatedAt: serverTimestamp(),
  });
  if (actor) {
    await logAdminAction({
      actorId: actor.id, actorName: actor.name, actorRole: actor.role,
      action: 'verify_coach', actionLabel: 'Verify Coach',
      targetType: 'user', targetCollection: 'users', targetId: coachId,
      targetLabel: data?.displayName || data?.email || coachId,
      changes: {
        'coachProfile.verificationStatus': { from: prevStatus, to: 'verified' },
        'coachProfile.verifiedAt': { from: prevVerifiedAt, to: verifiedAt },
        isVerified: { from: prevIsVerified, to: true },
      },
    });
  }
}

/**
 * Reject a coach's verification request
 */
export async function rejectCoachVerification(coachId: string, reason: string, actor?: Actor): Promise<void> {
  const userRef = doc(db, 'users', coachId);
  const snap = await getDoc(userRef);
  const data = snap.data();
  const prevStatus = data?.coachProfile?.verificationStatus ?? null;
  const prevReason = data?.coachProfile?.rejectionReason ?? null;
  const prevIsVerified = data?.isVerified ?? false;
  await updateDoc(userRef, {
    'coachProfile.verificationStatus': 'rejected' as CoachVerificationStatus,
    'coachProfile.rejectionReason': reason,
    isVerified: false,
    updatedAt: serverTimestamp(),
  });
  if (actor) {
    await logAdminAction({
      actorId: actor.id, actorName: actor.name, actorRole: actor.role,
      action: 'reject_coach', actionLabel: 'Reject Coach Verification',
      targetType: 'user', targetCollection: 'users', targetId: coachId,
      targetLabel: data?.displayName || data?.email || coachId,
      changes: {
        'coachProfile.verificationStatus': { from: prevStatus, to: 'rejected' },
        'coachProfile.rejectionReason': { from: prevReason, to: reason },
        isVerified: { from: prevIsVerified, to: false },
      },
    });
  }
}

/**
 * Get all coaches with pending verification status
 */
export async function getPendingCoachVerifications(): Promise<AdminUserData[]> {
  const q = query(
    collection(db, 'users'),
    where('coachProfile.verificationStatus', '==', 'pending'),
    orderBy('coachProfile.verificationRequestedAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    ...doc.data(),
    uid: doc.id,
  })) as AdminUserData[];
}

/**
 * Get all users with optional filtering including verification status
 */
export async function getUsersWithVerificationFilter(
  verificationStatus?: CoachVerificationStatus
): Promise<AdminUserData[]> {
  let q;

  if (verificationStatus) {
    q = query(
      collection(db, 'users'),
      where('coachProfile.verificationStatus', '==', verificationStatus),
      orderBy('createdAt', 'desc')
    );
  } else {
    q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    ...doc.data(),
    uid: doc.id,
  })) as AdminUserData[];
}

/**
 * Get all lessons with details (reads from lessons collection)
 */
export async function getAllLessons(statusFilter?: string): Promise<LessonData[]> {
  let q;
  if (statusFilter) {
    q = query(
      collection(db, 'lessons'),
      where('status', '==', statusFilter),
      orderBy('createdAt', 'desc'),
      limit(500)
    );
  } else {
    q = query(
      collection(db, 'lessons'),
      orderBy('createdAt', 'desc'),
      limit(500)
    );
  }

  const snapshot = await getDocs(q);
  /* LessonData types these as ISO strings, but a raw doc holds Firestore
     Timestamps. Unconverted, anything that did `new Date(value)` got Invalid
     Date, which is how the fullscreen lesson title read "Invalid Date" here.
     Same fields services/lessons.ts converts, so the admin copy of a lesson
     matches the one players and coaches load. */
  const timestampFields = ['createdAt', 'updatedAt', 'reservedAt', 'deliveredAt', 'playerViewedAt', 'submissionStartedAt'];
  const lessons = snapshot.docs.map(doc => {
    const data: Record<string, unknown> = { id: doc.id, ...doc.data() };
    for (const field of timestampFields) {
      const v = data[field];
      if (v instanceof Timestamp) data[field] = v.toDate().toISOString();
    }
    return data;
  }) as unknown as LessonData[];

  // Lesson docs don't store orderType or totalLessons — hydrate both from parent orders.
  // Batch into groups of 30 (Firestore 'in' query limit).
  const orderIds = [...new Set(lessons.map(l => l.orderId).filter(Boolean))] as string[];
  const orderTypeMap = new Map<string, string>();
  const orderTotalLessonsMap = new Map<string, number>();
  const orderCoachIdMap = new Map<string, string>();
  const orderCoachNameMap = new Map<string, string>();
  const refundedOrderIds = new Set<string>();
  for (let i = 0; i < orderIds.length; i += 30) {
    const batch = orderIds.slice(i, i + 30);
    const orderSnaps = await getDocs(
      query(collection(db, 'orders'), where('__name__', 'in', batch))
    );
    orderSnaps.forEach(s => {
      const d = s.data() as { type?: string; totalLessons?: number; coachId?: string; coachName?: string; refundRequested?: boolean; paymentStatus?: string };
      if (d.type) orderTypeMap.set(s.id, d.type);
      if (d.totalLessons != null) orderTotalLessonsMap.set(s.id, d.totalLessons);
      if (d.coachId) orderCoachIdMap.set(s.id, d.coachId);
      if (d.coachName) orderCoachNameMap.set(s.id, d.coachName);
      if (d.refundRequested || d.paymentStatus === 'refunded') refundedOrderIds.add(s.id);
    });
  }

  return lessons.map(l => ({
    ...l,
    orderType: l.orderId ? (orderTypeMap.get(l.orderId) ?? l.orderType) : l.orderType,
    totalLessons: l.orderId ? (orderTotalLessonsMap.get(l.orderId) ?? l.totalLessons) : l.totalLessons,
    orderCoachId: l.orderId ? orderCoachIdMap.get(l.orderId) : undefined,
    orderCoachName: l.orderId ? orderCoachNameMap.get(l.orderId) : undefined,
    orderRefunded: l.orderId ? refundedOrderIds.has(l.orderId) : false,
  }));
}

/**
 * Update lesson status (admin override)
 */
export async function updateLessonStatus(
  lessonId: string,
  status: 'reserved' | 'in_progress' | 'delivered'
): Promise<void> {
  const lessonRef = doc(db, 'lessons', lessonId);
  await updateDoc(lessonRef, {
    status,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Reset a delivered lesson back to in_progress, clearing all coach delivery data.
 * Use when a coach's response video is corrupted or they need to redo the lesson.
 */
export async function resetLessonForCoach(lessonId: string): Promise<void> {
  const lessonRef = doc(db, 'lessons', lessonId);
  await updateDoc(lessonRef, {
    status: 'in_progress',
    deliveredAt: deleteField(),
    publishedOriginalVideoId: deleteField(),
    publishedDraftId: deleteField(),
    coachNotes: deleteField(),
    coachResponseVideoDuration: deleteField(),
    attachedDrills: deleteField(),
    playerViewedAt: deleteField(),
    updatedAt: new Date().toISOString(),
  });
}

// ── Coach transfer ────────────────────────────────────────────────────────────

export interface TransferLessonOptions {
  lessonId: string;
  newCoachId: string;
  adminId: string;
  adminName?: string;
  note?: string;
  /** Override per-lesson pay for the incoming coach (cents). Defaults to offering price. */
  payPerLessonCents?: number;
  /** When true, skips sending the Double Bagel admin message. Used by transferOrder which sends its own. */
  skipNotification?: boolean;
}

/**
 * Transfers a lesson to a new coach.
 *
 * What this does:
 *   1. Validates lesson + new coach exist
 *   2. Gets or creates a conversation between the player and the new coach
 *   3. Appends an immutable history event to lessons/{lessonId}/history
 *   4. Updates the lesson document (coachId, coachName, coachAvatarUrl, conversationId,
 *      isTransferred, transferredAt, transferredFromCoachId/Name, transferNote)
 *   5. For single-lesson orders: also updates the parent order's coach fields
 *
 * Multiple transfers are fully supported — each creates a new history event.
 * Rollback = call this again with the original coachId.
 */
export async function transferLesson(options: TransferLessonOptions): Promise<void> {
  const { lessonId, newCoachId, adminId, adminName, note, payPerLessonCents, skipNotification } = options;

  // 1. Fetch lesson
  const lessonSnap = await getDoc(doc(db, 'lessons', lessonId));
  if (!lessonSnap.exists()) throw new Error(`Lesson ${lessonId} not found`);
  const lesson = { id: lessonId, ...lessonSnap.data() } as unknown as {
    coachId: string;
    playerId: string;
    orderId: string;
    conversationId: string;
    lessonNumber?: number;
    status: string;
    publishedDraftId?: string;
    deliveredAt?: string;
    playerVideoUrl?: string;
  };

  if (lesson.coachId === newCoachId) throw new Error('New coach is already assigned to this lesson');

  // 2. Fetch new coach profile
  const newCoachSnap = await getDoc(doc(db, 'users', newCoachId));
  if (!newCoachSnap.exists()) throw new Error(`Coach ${newCoachId} not found`);
  const newCoach = newCoachSnap.data() as UserProfile;
  const newCoachName =
    newCoach.coachProfile?.displayName || newCoach.displayName || 'Coach';
  const newCoachAvatarUrl =
    newCoach.coachProfile?.avatarUrl || newCoach.avatarUrl || undefined;

  // 3. Fetch current (from) coach name for the audit record
  const fromCoachSnap = await getDoc(doc(db, 'users', lesson.coachId));
  const fromCoachName = fromCoachSnap.exists()
    ? ((fromCoachSnap.data() as UserProfile).coachProfile?.displayName ||
       (fromCoachSnap.data() as UserProfile).displayName || 'Unknown Coach')
    : 'Unknown Coach';

  // 4. Fetch parent order for bundle context + single-lesson order update
  const orderSnap = await getDoc(doc(db, 'orders', lesson.orderId));
  if (!orderSnap.exists()) throw new Error(`Order ${lesson.orderId} not found`);
  const order = orderSnap.data() as {
    type: string;
    lessonIds: string[];
    coachId: string;
    totalLessons?: number;
    playerName?: string;
  };

  // Count already-delivered lessons in the order (for audit context)
  const deliveredCount = await (async () => {
    if (order.lessonIds.length <= 1) return 0;
    const snaps = await Promise.all(
      order.lessonIds.filter(id => id !== lessonId).map(id => getDoc(doc(db, 'lessons', id)))
    );
    return snaps.filter(s => s.exists() && s.data()?.status === 'delivered').length;
  })();

  // 5. Get or create conversation between player and new coach
  const newConversationId = await createConversation(lesson.playerId, newCoachId);

  const now = new Date().toISOString();

  // 6. Write immutable history event
  const historyRef = doc(collection(db, 'lessons', lessonId, 'history'));
  const historyEvent: Omit<LessonHistoryEvent, 'id'> = {
    type: 'coach_transfer',
    createdAt: now,
    adminId,
    ...(adminName && { adminName }),
    ...(note && { note }),
    fromCoachId: lesson.coachId,
    fromCoachName,
    toCoachId: newCoachId,
    toCoachName: newCoachName,
    fromConversationId: lesson.conversationId,
    toConversationId: newConversationId,
    ...(lesson.lessonNumber !== undefined && { lessonNumber: lesson.lessonNumber }),
    ...(order.totalLessons !== undefined && { totalLessons: order.totalLessons }),
    deliveredLessonsCount: deliveredCount,
    ...(payPerLessonCents !== undefined && { payPerLessonCents }),
  };
  await setDoc(historyRef, historyEvent);

  // 7. Update lesson document
  const lessonUpdate: Record<string, unknown> = {
    coachId: newCoachId,
    coachName: newCoachName,
    conversationId: newConversationId,
    isTransferred: true,
    transferredAt: now,
    transferredFromCoachId: lesson.coachId,
    transferredFromCoachName: fromCoachName,
    updatedAt: serverTimestamp(),
    ...(newCoachAvatarUrl !== undefined && { coachAvatarUrl: newCoachAvatarUrl }),
    ...(note !== undefined && { transferNote: note }),
    ...(payPerLessonCents !== undefined && { payPerLessonCents }),
  };
  await updateDoc(doc(db, 'lessons', lessonId), lessonUpdate);

  // 8. For single-lesson orders, update the order's denormalized coach fields too
  const isSingleLesson = order.type !== 'training_program' || order.lessonIds.length === 1;
  if (isSingleLesson) {
    const orderUpdate: Record<string, unknown> = {
      coachId: newCoachId,
      coachName: newCoachName,
      conversationId: newConversationId,
      updatedAt: serverTimestamp(),
      ...(newCoachAvatarUrl !== undefined && { coachAvatarUrl: newCoachAvatarUrl }),
    };
    await updateDoc(doc(db, 'orders', lesson.orderId), orderUpdate);
  }

  // 9. Notify the new coach via Double Bagel admin message (skipped when called from transferOrder,
  //    which sends its own order-level notification covering all transferred lessons at once).
  if (!skipNotification) {
    const coachFirstName = newCoachName.split(' ')[0] || newCoachName;
    const playerNameForMsg = order.playerName || 'The player';

    let msgText: string;
    if (order.type === 'training_program') {
      const lessonNum = lesson.lessonNumber ?? '?';
      const totalLess = order.totalLessons ?? '?';
      const payStr = payPerLessonCents != null ? ` Your compensation is $${(payPerLessonCents / 100).toFixed(0)} per lesson.` : '';
      msgText = `Hi ${coachFirstName}, we've assigned a new player to you. ${playerNameForMsg} was previously working with a coach who became unavailable. We've re-routed their Training Program to you. They're on Lesson ${lessonNum} of ${totalLess}. We've placed the lesson in your queue. Review the previous lessons to pick up where things left off. ${playerNameForMsg} has been notified of the new coach. They don't know the details of why the switch happened.${payStr} If you have any questions, reply here and our team will get back to you quickly. Thank you for taking this on.`;
    } else {
      const typeLabel = order.type === 'shot_consult' ? 'Shot Consult' : 'Single Lesson';
      const payStr = payPerLessonCents != null ? ` Your compensation is $${(payPerLessonCents / 100).toFixed(0)}.` : '';
      msgText = `Hi ${coachFirstName}, we've reassigned a ${typeLabel} from ${playerNameForMsg} to you. The original coach was unable to complete it. The lesson is now in your queue and ready for review.${payStr} If you have any questions, reply here and our team will get back to you quickly. Thank you for stepping in.`;
    }

    const supportConvId = await getOrCreateSupportConversation(newCoachId, newCoachName, newCoachAvatarUrl);
    const notifMsgRef = doc(collection(db, 'conversations', supportConvId, 'messages'));
    await setDoc(notifMsgRef, {
      id: notifMsgRef.id,
      senderId: DOUBLE_BAGEL_UID,
      senderName: 'Double Bagel',
      type: 'text',
      text: msgText,
      createdAt: serverTimestamp(),
      readBy: [DOUBLE_BAGEL_UID],
    });
    const preview = msgText.length > 120 ? msgText.substring(0, 120) + '…' : msgText;
    await updateDoc(doc(db, 'conversations', supportConvId), {
      lastMessage: preview,
      lastMessageAt: serverTimestamp(),
      lastMessageSenderId: DOUBLE_BAGEL_UID,
      [`unreadCount.${newCoachId}`]: increment(1),
      updatedAt: serverTimestamp(),
    });
  }
}

export interface TransferOrderOptions {
  orderId: string;
  newCoachId: string;
  adminId: string;
  adminName?: string;
  note?: string;
  /** Override per-lesson pay for the incoming coach (cents). Defaults to offering price. */
  payPerLessonCents?: number;
}

/**
 * Transfers a training program order to a new coach.
 *
 * What this does:
 *   1. Transfers every non-delivered lesson via transferLesson (history events, conversation
 *      creation, and lesson doc updates handled there).
 *   2. Writes one OrderHistoryEvent to orders/{orderId}/history for the order-level audit trail.
 *   3. Updates the order document's denormalized coach fields so future lessons
 *      (not yet created) and the player-facing UI reflect the new coach.
 */
export async function transferOrder(options: TransferOrderOptions): Promise<void> {
  const { orderId, newCoachId, adminId, adminName, note, payPerLessonCents } = options;

  // 1. Fetch order
  const orderSnap = await getDoc(doc(db, 'orders', orderId));
  if (!orderSnap.exists()) throw new Error(`Order ${orderId} not found`);
  const order = orderSnap.data() as { lessonIds?: string[]; totalLessons?: number; coachId: string; playerName?: string };

  // 2. Classify lessons — query by orderId directly rather than trusting order.lessonIds,
  //    which may be stale or empty (e.g. lazy-created lessons not yet appended to the array).
  const lessonsSnap = await getDocs(
    query(collection(db, 'lessons'), where('orderId', '==', orderId))
  );
  const delivered = lessonsSnap.docs.filter(s => s.data()?.status === 'delivered');
  const pending = lessonsSnap.docs.filter(s => s.data()?.status !== 'delivered');

  // 3. Transfer each pending lesson (creates per-lesson history + updates lesson docs).
  //    skipNotification=true because transferOrder sends one consolidated message below.
  for (const snap of pending) {
    await transferLesson({ lessonId: snap.id, newCoachId, adminId, adminName, note, payPerLessonCents, skipNotification: true });
  }

  // 4. Fetch new coach profile
  const newCoachSnap = await getDoc(doc(db, 'users', newCoachId));
  if (!newCoachSnap.exists()) throw new Error(`Coach ${newCoachId} not found`);
  const newCoach = newCoachSnap.data() as UserProfile;
  const newCoachName = newCoach.coachProfile?.displayName || newCoach.displayName || 'Coach';
  const newCoachAvatarUrl = newCoach.coachProfile?.avatarUrl || newCoach.avatarUrl;

  // 5. Fetch current coach name for history
  const fromCoachSnap = await getDoc(doc(db, 'users', order.coachId));
  const fromCoach = fromCoachSnap.exists() ? (fromCoachSnap.data() as UserProfile) : null;
  const fromCoachName = fromCoach?.coachProfile?.displayName || fromCoach?.displayName || 'Unknown Coach';

  // 6. Write order-level history event
  const now = new Date().toISOString();
  const orderHistoryRef = doc(collection(db, 'orders', orderId, 'history'));
  const historyEvent: Omit<OrderHistoryEvent, 'id'> = {
    type: 'coach_transfer',
    createdAt: now,
    adminId,
    ...(adminName && { adminName }),
    ...(note && { note }),
    fromCoachId: order.coachId,
    fromCoachName,
    toCoachId: newCoachId,
    toCoachName: newCoachName,
    lessonsTransferred: pending.length,
    deliveredLessonsCount: delivered.length,
    ...(order.totalLessons !== undefined && { totalLessons: order.totalLessons }),
    ...(payPerLessonCents !== undefined && { payPerLessonCents }),
  };
  await setDoc(orderHistoryRef, historyEvent);

  // 7. Update order's denormalized coach fields + transfer state
  await updateDoc(doc(db, 'orders', orderId), {
    coachId: newCoachId,
    coachName: newCoachName,
    isTransferred: true,
    transferredFromCoachId: order.coachId,
    transferredFromCoachName: fromCoachName,
    ...(note && { transferNote: note }),
    updatedAt: serverTimestamp(),
    ...(newCoachAvatarUrl && { coachAvatarUrl: newCoachAvatarUrl }),
  });

  // 8. Send one consolidated admin notification to the new coach
  const coachFirstName = newCoachName.split(' ')[0] || newCoachName;
  const playerNameForMsg = order.playerName || 'The player';
  const firstPendingLessonNum = (pending[0]?.data() as { lessonNumber?: number } | undefined)?.lessonNumber;
  const lessonNum = firstPendingLessonNum ?? (delivered.length + 1);
  const totalLess = order.totalLessons ?? (pending.length + delivered.length);
  const payStr = payPerLessonCents != null ? ` Your compensation is $${(payPerLessonCents / 100).toFixed(0)} per lesson.` : '';
  const msgText = `Hi ${coachFirstName}, we've assigned a new player to you. ${playerNameForMsg} was previously working with a coach who became unavailable. We've re-routed their Training Program to you. They're on Lesson ${lessonNum} of ${totalLess}. We've placed the lesson in your queue. Review the previous lessons to pick up where things left off. ${playerNameForMsg} has been notified of the new coach. They don't know the details of why the switch happened.${payStr} If you have any questions, reply here and our team will get back to you quickly. Thank you for taking this on.`;

  const supportConvId = await getOrCreateSupportConversation(newCoachId, newCoachName, newCoachAvatarUrl);
  const notifMsgRef = doc(collection(db, 'conversations', supportConvId, 'messages'));
  await setDoc(notifMsgRef, {
    id: notifMsgRef.id,
    senderId: DOUBLE_BAGEL_UID,
    senderName: 'Double Bagel',
    type: 'text',
    text: msgText,
    createdAt: serverTimestamp(),
    readBy: [DOUBLE_BAGEL_UID],
  });
  const preview = msgText.length > 120 ? msgText.substring(0, 120) + '…' : msgText;
  await updateDoc(doc(db, 'conversations', supportConvId), {
    lastMessage: preview,
    lastMessageAt: serverTimestamp(),
    lastMessageSenderId: DOUBLE_BAGEL_UID,
    [`unreadCount.${newCoachId}`]: increment(1),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Returns the full transfer history for an order, oldest first.
 */
export async function getOrderHistory(orderId: string): Promise<OrderHistoryEvent[]> {
  const q = query(
    collection(db, 'orders', orderId, 'history'),
    orderBy('createdAt', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as OrderHistoryEvent);
}

/**
 * Returns the full transfer history for a lesson, oldest first.
 * Use this to build an audit report or power a "rollback" UI.
 */
export async function getLessonHistory(lessonId: string): Promise<LessonHistoryEvent[]> {
  const q = query(
    collection(db, 'lessons', lessonId, 'history'),
    orderBy('createdAt', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as LessonHistoryEvent);
}

/**
 * Convenience: reverts the most recent transfer by transferring back to the previous coach.
 * Creates a new history event — the audit trail is never modified.
 */
export async function rollbackLastTransfer(
  lessonId: string,
  adminId: string,
  adminName?: string,
  note?: string
): Promise<void> {
  const history = await getLessonHistory(lessonId);
  const last = [...history].reverse().find(e => e.type === 'coach_transfer');
  if (!last) throw new Error('No transfer history found for this lesson');
  await transferLesson({
    lessonId,
    newCoachId: last.fromCoachId,
    adminId,
    adminName,
    note: note ?? `Rollback of transfer on ${last.createdAt}`,
  });
}

/**
 * Get all conversations/chats
 */
export async function getAllChats(): Promise<ChatData[]> {
  const q = query(collection(db, 'conversations'), orderBy('lastMessageAt', 'desc'), limit(500));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as ChatData[];
}

/**
 * Get messages for a conversation
 */
export async function getChatMessages(conversationId: string) {
  const messagesRef = collection(db, 'conversations', conversationId, 'messages');
  const q = query(messagesRef, orderBy('createdAt', 'asc'), limit(500));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));
}

/**
 * Get basic metrics for dashboard
 */
export async function getMetrics(): Promise<MetricsData> {
  // Get all users
  const usersSnapshot = await getDocs(collection(db, 'users'));
  const users = usersSnapshot.docs.map(doc => doc.data());

  const totalUsers = users.length;
  const totalCoaches = users.filter(u => u.accountType === 'coach').length;
  const totalPlayers = users.filter(u => u.accountType === 'player').length;

  // Get all lessons (from lessons collection)
  const lessonsSnapshot = await getDocs(collection(db, 'lessons'));
  const lessons = lessonsSnapshot.docs.map(doc => doc.data());

  const totalLessons = lessons.filter(l => l.status === 'delivered').length;

  // Count lessons per coach
  const lessonsPerCoach: Record<string, number> = {};
  lessons.forEach(lesson => {
    if (lesson.coachId && lesson.status === 'completed') {
      lessonsPerCoach[lesson.coachId] = (lessonsPerCoach[lesson.coachId] || 0) + 1;
    }
  });

  // Count lessons per player
  const lessonsPerPlayer: Record<string, number> = {};
  lessons.forEach(lesson => {
    if (lesson.playerId && lesson.status === 'completed') {
      lessonsPerPlayer[lesson.playerId] = (lessonsPerPlayer[lesson.playerId] || 0) + 1;
    }
  });

  return {
    totalLessons,
    lessonsPerCoach,
    lessonsPerPlayer,
    totalUsers,
    totalCoaches,
    totalPlayers,
  };
}

/**
 * Get user by ID
 */
export async function getUserById(userId: string): Promise<AdminUserData | null> {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    return null;
  }

  return {
    ...userSnap.data(),
    uid: userSnap.id,
  } as AdminUserData;
}

// ── Stripe admin ──────────────────────────────────────────────────────────────

export interface StripeAdminLesson {
  id: string;
  orderId?: string;
  orderType?: string;
  lessonNumber?: number;
  coachId: string;
  coachName: string;
  playerId: string;
  playerName: string;
  offeringTitle?: string;
  status: string;
  paymentStatus?: 'unpaid' | 'authorized' | 'captured' | 'transferred' | 'refunded';
  /** Stripe transfer ID for this lesson's coach payout (lives on lesson doc). */
  transferId?: string;
  /** Payment method used for this lesson. 'credits' = DB-funded. */
  paymentMethod?: 'stripe' | 'credits';
  /** Per-lesson price in cents at time of booking. */
  priceCents: number;
  createdAt: string;
  deliveredAt?: string;
}

/**
 * Fetch all lessons that have a Stripe paymentIntentId — used for the admin Billing page.
 * Computes summary stats and surfaces refund/payout action items.
 */
export async function getStripeAdminLessons(): Promise<StripeAdminLesson[]> {
  // Primary query: lessons that already have paymentStatus set
  const q = query(
    collection(db, 'lessons'),
    where('paymentStatus', 'in', ['captured', 'transferred', 'refunded', 'authorized']),
    limit(1000)
  );

  // Secondary query: delivered lessons that have no paymentStatus (training program
  // continuation lessons 2-4 created before the Cloud Function was updated to inherit
  // paymentStatus from the parent order).
  const deliveredQ = query(
    collection(db, 'lessons'),
    where('status', '==', 'delivered'),
    limit(1000)
  );

  const [snapshot, deliveredSnap] = await Promise.all([getDocs(q), getDocs(deliveredQ)]);

  const normalize = (d: import('firebase/firestore').QueryDocumentSnapshot) => {
    const data = d.data() as Omit<StripeAdminLesson, 'id'> & { amountCents?: number; price?: number; priceValue?: number };
    return {
      id: d.id,
      ...data,
      priceCents: data.priceCents ?? data.amountCents ?? data.price ?? data.priceValue ?? 0,
    } as StripeAdminLesson;
  };

  const seen = new Set<string>();
  const results: StripeAdminLesson[] = [];

  for (const d of snapshot.docs) {
    seen.add(d.id);
    results.push(normalize(d));
  }

  // Add delivered lessons that weren't already found (missing paymentStatus).
  // Only include them if they have a priceCents > 0 (i.e. came from a paid order).
  for (const d of deliveredSnap.docs) {
    if (seen.has(d.id)) continue;
    const lesson = normalize(d);
    if ((lesson.priceCents ?? 0) > 0) {
      // Treat as captured — the order was paid, payout just hasn't happened yet
      lesson.paymentStatus = 'captured';
      results.push(lesson);
    }
  }

  const ms = (v: unknown) => v instanceof Object && 'toMillis' in v
    ? (v as { toMillis(): number }).toMillis()
    : new Date(v as string).getTime();

  return results.sort((a, b) => ms(b.createdAt) - ms(a.createdAt));
}

/**
 * Fetch paid lessons for a specific coach — used by the Earnings page.
 * Mirrors getStripeAdminLessons but scoped to one coachId.
 */
export async function getStripeCoachLessons(coachId: string): Promise<StripeAdminLesson[]> {
  const q = query(
    collection(db, 'lessons'),
    where('coachId', '==', coachId),
    where('paymentStatus', 'in', ['captured', 'transferred', 'refunded', 'authorized']),
    limit(500)
  );
  const deliveredQ = query(
    collection(db, 'lessons'),
    where('coachId', '==', coachId),
    where('status', '==', 'delivered'),
    limit(500)
  );

  const [snapshot, deliveredSnap] = await Promise.all([getDocs(q), getDocs(deliveredQ)]);

  const normalize = (d: import('firebase/firestore').QueryDocumentSnapshot) => {
    const data = d.data() as Omit<StripeAdminLesson, 'id'> & { amountCents?: number; price?: number; priceValue?: number };
    return {
      id: d.id,
      ...data,
      priceCents: data.priceCents ?? data.amountCents ?? data.price ?? data.priceValue ?? 0,
    } as StripeAdminLesson;
  };

  const seen = new Set<string>();
  const results: StripeAdminLesson[] = [];

  for (const d of snapshot.docs) {
    seen.add(d.id);
    results.push(normalize(d));
  }

  for (const d of deliveredSnap.docs) {
    if (seen.has(d.id)) continue;
    const lesson = normalize(d);
    if ((lesson.priceCents ?? 0) > 0) {
      lesson.paymentStatus = 'captured';
      results.push(lesson);
    }
  }

  const ms = (v: unknown) => v instanceof Object && 'toMillis' in v
    ? (v as { toMillis(): number }).toMillis()
    : new Date(v as string).getTime();

  return results.sort((a, b) => ms(b.createdAt) - ms(a.createdAt));
}

export interface TrainingProgramRefundRequest {
  id: string;
  coachId: string;
  coachName: string;
  playerId: string;
  playerName: string;
  offeringTitle?: string;
  /** Per-lesson price × totalLessons = total charged. */
  priceCents: number;
  totalLessons?: number;
  refundRequestedAt?: string;
  refundRequestReason?: string;
  /** lessonIds[0] is lesson 1 — passed to refundLessonFn when admin issues the refund */
  lessonIds: string[];
  createdAt: string;
}

export interface PaidOrderRecord {
  id: string;
  paymentStatus: 'captured' | 'transferred' | 'refunded' | 'authorized';
  /** Per-lesson price in cents. */
  priceCents: number;
  totalLessons: number;
  /** Actual total charged = priceCents × totalLessons. */
  totalCents: number;
}

/**
 * Fetch all orders with a Stripe payment status.
 * Used for order-level summary stats on the Billing page (total collected, etc.).
 */
export async function getPaidOrders(): Promise<PaidOrderRecord[]> {
  const q = query(
    collection(db, 'orders'),
    where('paymentStatus', 'in', ['captured', 'transferred', 'refunded', 'authorized']),
    limit(1000),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => {
    const data = d.data();
    const priceCents = data.priceCents ?? data.amountCents ?? data.price ?? data.priceValue ?? 0;
    const totalLessons = data.totalLessons ?? 1;
    return {
      id: d.id,
      paymentStatus: data.paymentStatus,
      priceCents,
      totalLessons,
      totalCents: priceCents * totalLessons,
    } as PaidOrderRecord;
  });
}

export interface OrderRefundRecord {
  id: string;
  coachName: string;
  playerName: string;
  offeringTitle?: string;
  type?: string;
  /** Per-lesson price in cents. */
  priceCents: number;
  totalLessons: number;
  /** Actual total charged = priceCents × totalLessons. */
  totalCents: number;
  refundId?: string;
  lessonStatus?: string;
  createdAt: string;
}

/**
 * Fetch all orders with paymentStatus == 'refunded'.
 * Used for refund history and the Total Refunded stat on the Billing page.
 * Orders are the source of truth for refund amounts (training programs are one order, not per-lesson).
 */
export async function getRefundedOrders(): Promise<OrderRefundRecord[]> {
  const q = query(
    collection(db, 'orders'),
    where('paymentStatus', '==', 'refunded'),
    limit(1000),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(d => {
      const data = d.data();
      const priceCents = data.priceCents ?? data.amountCents ?? data.price ?? data.priceValue ?? 0;
      const totalLessons = data.totalLessons ?? 1;
      return {
        id: d.id,
        coachName: data.coachName ?? '',
        playerName: data.playerName ?? '',
        offeringTitle: data.offeringTitle,
        type: data.type,
        priceCents,
        totalLessons,
        totalCents: priceCents * totalLessons,
        refundId: data.refundId,
        createdAt: data.createdAt?.toDate?.().toISOString() ?? data.createdAt ?? '',
      } as OrderRefundRecord;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

/**
 * Fetch training program orders where the player has requested a refund and admin
 * has not yet processed it. Used for the admin Billing → Refunds tab.
 */
export async function getTrainingProgramRefundRequests(): Promise<TrainingProgramRefundRequest[]> {
  const q = query(
    collection(db, 'orders'),
    where('refundRequested', '==', true),
    where('refundStatus', '==', 'pending'),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      coachId: data.coachId,
      coachName: data.coachName,
      playerId: data.playerId,
      playerName: data.playerName,
      offeringTitle: data.offeringTitle,
      priceCents: data.priceCents ?? data.amountCents ?? data.price ?? data.priceValue ?? 0,
      totalLessons: data.totalLessons,
      refundRequestedAt: data.refundRequestedAt?.toDate?.().toISOString() ?? data.refundRequestedAt,
      refundRequestReason: data.refundRequestReason,
      lessonIds: data.lessonIds ?? [],
      createdAt: data.createdAt?.toDate?.().toISOString() ?? data.createdAt,
    } as TrainingProgramRefundRequest;
  });
}

/**
 * Restore a lesson to its previous status (before system auto-transition)
 * Calls the adminRestoreLessonStatus Cloud Function
 */
export async function restoreLessonStatus(lessonId: string): Promise<{ success: boolean; restoredStatus: string }> {
  const restoreFunction = httpsCallable<{ lessonId: string }, { success: boolean; lessonId: string; restoredStatus: string }>(
    functions,
    'adminRestoreLessonStatus'
  );

  const result = await restoreFunction({ lessonId });
  return result.data;
}

/**
 * Deactivate or reactivate a staff member.
 * Updates Firestore isActive and disables/enables the Firebase Auth account.
 * Root-only — enforced server-side.
 */
export async function deactivateStaffMember(uid: string, isActive: boolean): Promise<void> {
  const fn = httpsCallable(functions, 'adminDeactivateStaffMember');
  await fn({ uid, isActive });
}

/**
 * Permanently delete a staff member from Firestore and Firebase Auth.
 * Historical data (messages, action log) is preserved — names are denormalized.
 * Root-only — enforced server-side.
 */
export async function deleteStaffMember(uid: string): Promise<void> {
  const fn = httpsCallable(functions, 'adminDeleteStaffMember');
  await fn({ uid });
}

/**
 * Re-run video composition for a lesson from its published draft. Starts the
 * video-compose Cloud Run Job through the staff-only retryLessonVideo function.
 */
export async function retryVideoProcessing(lessonId: string): Promise<void> {
  const fn = httpsCallable(functions, 'retryLessonVideo');
  await fn({ lessonId });
}
